import Phaser from "phaser";
import F84Game from "../Game";

export enum ScaleBy {
	WIDTH,
	HEIGHT,
}

export enum OrientationType {
	PORTRAIT,
	LANDSCAPE,
}

export default class ScaleManager extends Phaser.Events.EventEmitter {
	private _game: F84Game;
	private _orientation: OrientationType;
	private _scaleBy: ScaleBy;

	private _lastResizeSize: Phaser.Math.Vector2;

	constructor(game: F84Game, orientation: OrientationType, scaleBy: ScaleBy) {
		super();

		this._game = game;
		this._orientation = orientation;
		this._scaleBy = scaleBy;

		this._game.scale.on(Phaser.Scale.Events.RESIZE, this.onPhaserResize, this);
		this._game.scale.on(Phaser.Scale.Events.LEAVE_FULLSCREEN, this.onPhaserResize, this);
		this._game.scale.on(Phaser.Scale.Events.ENTER_FULLSCREEN, this.onPhaserResize, this);
		this._game.scale.on(Phaser.Scale.Events.ORIENTATION_CHANGE, this.onPhaserResize, this);
		this._game.events.on(Phaser.Scenes.Events.CREATE, this.onPhaserSceneCreate, this);
		this.onPhaserResize();
	}

	public static ScaleBackgroundFromZone(
		background: Phaser.GameObjects.Image | Phaser.GameObjects.Container,
		zone: Phaser.GameObjects.Zone
	): void {
		ScaleManager.ScaleBackground(background, zone.width, zone.height);
	}

	public static ScaleBackground(
		background: Phaser.GameObjects.Image | Phaser.GameObjects.Container,
		width: number,
		height: number
	): void {
		const widthRatio = width / background.width;
		const heightRatio = height / background.height;
		const scale = Math.max(widthRatio, heightRatio);
		background.setScale(scale);
	}

	public static MatchWidth(background: Phaser.GameObjects.Image, width: number): void {
		background.setScale(width / background.width);
	}

	public static MatchHeight(background: Phaser.GameObjects.Image, height: number): void {
		background.setScale(height / background.height);
	}

	public update(): void {
		const parentSize = new Phaser.Math.Vector2(
			Math.min(window.innerWidth, window.outerWidth),
			Math.min(window.innerHeight, window.outerHeight)
		);
		if (this._lastResizeSize != null && (this._lastResizeSize.x != parentSize.x || this._lastResizeSize.y != parentSize.y)) {
			this.onPhaserResize();
		}
		this.updateRotationDisplay();
	}

	private onPhaserResize(): void {
		const parentSize = new Phaser.Math.Vector2(
			Math.min(window.innerWidth, window.outerWidth),
			Math.min(window.innerHeight, outerHeight)
		);
		if (this._lastResizeSize == null || parentSize.x != this._lastResizeSize.x || parentSize.y != this._lastResizeSize.y) {
			this._lastResizeSize = parentSize.clone();
			this.resize();
		}
	}

	// Scale to base resolution, taking orientation of the device into account.
	private resize(): void {
		const desiredWidth: number = this._game.DefaultWidth;
		const desiredHeight: number = this._game.DefaultHeight;

		let currentWidth: number = Math.min(window.innerWidth, window.outerWidth);
		let currentHeight: number = Math.min(window.innerHeight, window.outerHeight);

		currentWidth *= F84Game.Instance.DevicePixelRatio;
		currentHeight *= F84Game.Instance.DevicePixelRatio;

		const widthRatio: number = currentWidth / desiredWidth;
		const heightRatio: number = currentHeight / desiredHeight;

		let width: number;
		let height: number;
		let ratio: number;

		if (this._scaleBy === ScaleBy.HEIGHT) {
			width = currentWidth / heightRatio;
			height = desiredHeight;
			ratio = heightRatio;
		} else {
			width = desiredWidth;
			height = currentHeight / widthRatio;
			ratio = widthRatio;
		}

		if (ratio < 1) {
			this._game.scale.resize(width * ratio, height * ratio);
			this._game.scale.gameSize.setWidth(width);
			this._game.scale.gameSize.setHeight(height);
		} else {
			this._game.scale.setGameSize(width, height);
		}

		this._game.scale.setZoom(ratio / F84Game.Instance.DevicePixelRatio);

		const zoomedWidth = width * heightRatio;
		const zoomedHeight = height * heightRatio;

		if (this._game.scale.isFullscreen) {
			this._game.canvas.style.marginLeft = window.outerWidth - window.innerWidth + "px";
			this._game.canvas.style.marginTop = window.outerHeight - window.innerHeight + "px";
		} else {
			this._game.canvas.style.marginLeft = (currentWidth - zoomedWidth) / 2 + "px";
			this._game.canvas.style.marginTop = (currentHeight - zoomedHeight) / 2 + "px";
		}

		window.scrollTo(window.scrollX, 0); //fixes content getting stuck under address bar. noticed on Safari in iOS when exiting fullscreen

		this.updateRotationDisplay();
	}

	private onPhaserSceneCreate(): void {
		this.updateRotationDisplay();
	}

	private updateRotationDisplay(): void {
		if (!this._game.textures.exists("rotateDevice")) return;

		const sign = Math.sign(this._game.scale.width - this._game.scale.height);
		const sign2 = Math.sign(F84Game.Instance.DefaultWidth - F84Game.Instance.DefaultHeight);

		if (sign != sign2 && !this._game.scene.isActive("RotateDevice")) {
			this._game.scene.start("RotateDevice");
		}
	}
}
